import { useState, useEffect } from 'react'
import { X, Gift, Sparkles, PartyPopper } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useLoginModal } from '@/contexts/LoginModalContext'
import { getActiveReferralEvent } from '@/supabase/referral/repository/referralEventRepository'
import { isEventOpen, type ReferralEventState } from '@/lib/referralEvent'

interface ReferralLandingModalProps {
  referralCode: string | null
}

export default function ReferralLandingModal({ referralCode }: ReferralLandingModalProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [referralEvent, setReferralEvent] = useState<ReferralEventState>(undefined)
  const { openLoginModal } = useLoginModal()

  // 이벤트 조회 (실패 시 undefined 유지 = 혜택 줄 미노출)
  useEffect(() => {
    getActiveReferralEvent()
      .then(setReferralEvent)
      .catch((error) => console.error('[ReferralLandingModal] 이벤트 조회 오류:', error))
  }, [])

  useEffect(() => {
    if (referralCode) {
      setIsOpen(true)
    }
  }, [referralCode])

  const handleSignup = () => {
    setIsOpen(false)
    openLoginModal()
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-gradient-to-br from-green-50 via-white to-emerald-50 rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300 z-[10000]">
        {/* 닫기 버튼 - 왼쪽 위 */}
        <button
          onClick={handleClose}
          className="absolute top-4 left-4 p-2 rounded-full hover:bg-white/50 transition-colors z-10"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* 헤더 - 아이콘 & 이펙트 */}
        <div className="relative pt-8 pb-4 text-center overflow-hidden">
          {/* 배경 장식 */}
          <div className="absolute inset-0 flex items-center justify-center opacity-10">
            <Sparkles className="w-32 h-32 text-green-500 animate-pulse" />
          </div>
          
          {/* 메인 아이콘 */}
          <div className="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full shadow-lg mb-4">
            <Gift className="w-10 h-10 text-white" />
            <div className="absolute -top-2 -right-2">
              <PartyPopper className="w-8 h-8 text-yellow-400 animate-bounce" />
            </div>
          </div>

          {/* 제목 */}
          <h2 className="text-2xl font-bold mb-2">
            <span className="text-2xl">🎁</span>{' '}
            <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">초대장으로 오셨군요!</span>
          </h2>
          <p className="text-gray-600 px-6">
            친구의 추천으로 특별한 혜택을 받으세요
          </p>
        </div>

        {/* 본문 */}
        <div className="px-6 pb-6 space-y-4">
          {/* 혜택 안내 카드 */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border-2 border-green-200">
            <div className="text-center space-y-3">
              <p className="text-4xl font-bold text-green-600"> 90% 할인</p>
              <div className="text-sm text-gray-600 space-y-1">
                <p>스탠다드: <span className="line-through text-gray-400">₩9,900</span> → <span className="font-bold text-green-600">₩990</span></p>
                <p>프리미엄: <span className="line-through text-gray-400">₩24,900</span> → <span className="font-bold text-green-600">₩2,490</span></p>
              </div>
              {referralEvent !== undefined && (
                <p className="text-xs text-purple-600 font-semibold pt-2">
                  {isEventOpen(referralEvent)
                    ? `🔥 이벤트 기간! 첫 결제 후 승인 시 ${referralEvent.reward_multiplier}개월 추가 혜택${referralEvent.max_referees ? ` (결제 순서 기준 선착순 ${referralEvent.max_referees}명)` : ''}`
                    : '+ 첫 결제 시 1개월 추가 혜택'}
                </p>
              )}
            </div>
          </div>

          {/* 버튼 */}
          <Button
            onClick={handleSignup}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-xl transition-all text-lg"
          >
            지금 가입하고 혜택 받기 →
          </Button>
        </div>
      </div>
    </div>
  )
}
